import { makeAutoObservable, runInAction } from 'mobx'
import { isEqual, sortBy } from 'lodash'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type Integration, IntegrationsApi } from 'entities/Integrations'
import { SendFromMultiStore } from 'widgets/SendFromMulti'
import { type SendFromMultiStoreOptions } from 'widgets/SendFromMulti/store/SendFromMultiStore'

export class IntegrationIntercomConnectInboxStore {
  private _allTeamInboxesMap: Map<number, IResponseInbox> = new Map()
  private _loading = false
  private _sendFromMultiStore: SendFromMultiStore | null = null
  private _mappedIds: number[] = []

  constructor(private _integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    try {
      await this.fetchAllInboxes()

      this._sendFromMultiStore = this.createSendFromMultiStore()
      await this._sendFromMultiStore.initPromise

      await this.getMappings()
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get allTeamInboxes() {
    return Array.from(this._allTeamInboxesMap.values()).filter((item) => !item.is_rcs)
  }

  get loading() {
    return this._loading
  }

  get sendFromMultiStore() {
    return this._sendFromMultiStore
  }

  fetchAllInboxes = async () => {
    try {
      const { data } = await this._entities.inboxesStore.getTeamsAll()
      runInAction(() => {
        this._allTeamInboxesMap.clear()
        data.forEach((item) => {
          this._allTeamInboxesMap.set(item.id, item)
        })
      })
    } catch (error) {
      logger.error(error)
    }
  }

  createSendFromMultiStore = () => {
    const options: SendFromMultiStoreOptions = {
      getDropdownItemsOptions: {
        excludeShortCode: true,
        disabledClosedTooltip: true,
        hideChildren: true,
      },
      preloadedInboxes: this.allTeamInboxes,
      isInboxMode: true,
    }

    return new SendFromMultiStore(this._entities, options)
  }

  getMappings = async () => {
    try {
      const { data } = await IntegrationsApi.getMapInboxesToIntegrationChannels(
        this._integration.key
      )

      const providerIdentifier = this._integration?.user_credential?.provider_identifier
      const mappedIds = data.mapping[providerIdentifier ?? ''] ?? []

      if (mappedIds.length > 0) {
        await this._sendFromMultiStore?.setInboxIds(mappedIds)
        this._mappedIds = mappedIds
      }
    } catch (error) {
      logger.error(error)
    }
  }

  resetMappedIds() {
    showToast({
      type: 'error',
      title: 'Please map a Salesmsg inbox.',
    })

    this._mappedIds = []
  }

  updateMapping = async () => {
    const inboxes = this.sendFromMultiStore?.selectedInboxIds ?? []

    if (!inboxes.length) {
      this.resetMappedIds()
      return
    }

    if (isEqual(sortBy(this._mappedIds), sortBy(inboxes))) return

    try {
      await IntegrationsApi.addMessengerMapping({
        integration: this._integration.key,
        inboxes: inboxes,
      })

      showToast({
        type: 'success',
        title: 'Salesmsg–Intercom mapping updated',
      })

      this._mappedIds = inboxes
    } catch (error) {
      logger.error(error)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    }
  }
}
